import {
  ACCESS_TOKEN,
  ID_TOKEN,
  REFRESH_TOKEN,
  SUBJECT_OF_TOKEN,
  TRANSACTION_KEY
} from './constants';
import { AuthenticationError } from './model/AuthenticationError';
import { Cache } from './storage/cache';
import * as localStorage from './storage/localStorage';
import type { AuthClientOptions } from './types';
import { decode, verify as verifyIdToken } from './util/jwt';
import {
  bufferToBase64UrlEncoded,
  createRandomString,
  getUniqueScopes,
  getUrlParam,
  oauthToken,
  sha256
} from './util/utils';
import { isManagementViewEnabled } from '../../utilities/EnvironmentUtility';

const DEFAULT_SCOPE = 'openid';
const DEFAULT_AUDIENCE = 'undefined';

export class AuthClient {
  private readonly options: AuthClientOptions;
  private readonly scope: string;
  private readonly audience: string;
  private cache: Cache;
  private readonly authEndpoint: string;
  private readonly tokenEndpoint: string;
  private readonly logoutEndpoint: string;
  private readonly tokenIssuer: string;
  private readonly requiredRole: string | null;

  constructor(options: AuthClientOptions) {
    this.options = options;
    this.scope = getUniqueScopes(DEFAULT_SCOPE, options.scope);
    this.audience = DEFAULT_AUDIENCE;
    this.cache = new Cache();
    this.authEndpoint = this.options.authEndpoint;
    this.tokenEndpoint = this.options.tokenEndpoint;
    this.logoutEndpoint = this.options.logoutEndpoint;
    this.tokenIssuer = this.options.issuer;
    this.requiredRole = this.options.requiredRole;
  }

  _url(endpoint: string, params: Record<string, string>) {
    return (
      endpoint +
      (endpoint.indexOf('?') === -1 ? '?' : '&') +
      new URLSearchParams(params).toString()
    );
  }

  _getParams(state: string, nonce: string, code_challenge: string) {
    return {
      client_id: this.options.client_id,
      scope: this.scope,
      redirect_uri: this.options.redirect_uri,
      response_type: this.options.response_type,
      response_mode: this.options.response_mode,
      state,
      nonce,
      code_challenge,
      code_challenge_method: 'S256'
    };
  }

  _authorizeUrl(authorizeOptions: Record<string, string>) {
    return this._url(this.authEndpoint, authorizeOptions);
  }

  _verifyIdToken(id_token: string, nonce?: string) {
    return verifyIdToken({
      iss: this.tokenIssuer,
      aud: this.options.client_id,
      id_token,
      nonce
    });
  }

  private assertTokenPresent(token: string | undefined, tokenName: string) {
    if (!token) {
      throw new Error(`Missing ${tokenName} in authentication response`);
    }
    return token;
  }

  async buildAuthorizeUrl() {
    const stateIn = btoa(createRandomString());
    const nonceIn = createRandomString();
    const code_verifier = createRandomString();
    const code_challengeBuffer = await sha256(code_verifier);
    const code_challenge = bufferToBase64UrlEncoded(code_challengeBuffer);
    const params = this._getParams(stateIn, nonceIn, code_challenge);
    localStorage.setJsonItem(TRANSACTION_KEY, {
      nonce: nonceIn,
      code_verifier,
      scope: params.scope,
      audience: DEFAULT_AUDIENCE
    });
    return this._authorizeUrl(params);
  }

  getUser() {
    const cache = this.getCacheEntry();
    return (
      cache && {
        ...cache.decodedAccessToken.user,
        ...cache.decodedIdToken.user
      }
    );
  }

  async loginWithRedirect() {
    const url = await this.buildAuthorizeUrl();
    window.location.assign(url);
  }

  async handleRedirectCallback() {
    const state = getUrlParam('state') ?? '';
    const code = getUrlParam('code') ?? '';
    const error = getUrlParam('error');
    const error_description = getUrlParam('error_description') ?? '';

    if (error) {
      localStorage.removeItem(TRANSACTION_KEY);
      throw new AuthenticationError(error, error_description, state);
    }

    const transaction = localStorage.getJsonItem(TRANSACTION_KEY);
    if (!transaction) {
      throw new Error('Invalid state (transaction)');
    }
    localStorage.removeItem(TRANSACTION_KEY);

    const authResult = await oauthToken(this.tokenEndpoint, {
      grant_type: 'authorization_code',
      audience: DEFAULT_AUDIENCE,
      client_id: this.options.client_id,
      code_verifier: transaction.code_verifier,
      redirect_uri: this.options.redirect_uri,
      code
    });

    const idToken = this.assertTokenPresent(authResult.id_token, ID_TOKEN);
    localStorage.setItem(ID_TOKEN, idToken);

    const accessToken = authResult.access_token;
    if (!accessToken && isManagementViewEnabled()) {
      throw new Error(`Missing ${ACCESS_TOKEN} in authentication response`);
    }
    if (accessToken) {
      localStorage.setItem(ACCESS_TOKEN, accessToken);
    }

    if (authResult.refresh_token) {
      localStorage.setItem(REFRESH_TOKEN, authResult.refresh_token);
    }

    const decodedIdToken = this._verifyIdToken(idToken, transaction.nonce);
    decodedIdToken.user.id = decodedIdToken.user[SUBJECT_OF_TOKEN];

    const decodedAccessToken = accessToken
      ? decode(accessToken)
      : decodedIdToken;
    if (
      !this.userHasRequiredScopeRole(decodedAccessToken) &&
      !this.userHasRequiredScopeRole(decodedIdToken)
    ) {
      throw new Error('Unauthorized user');
    }

    const cacheEntry = {
      ...authResult,
      decodedIdToken,
      decodedAccessToken,
      audience: transaction.audience,
      scope: transaction.scope
    };
    this.cache.save(cacheEntry);

    window.authCache = {
      ...authResult,
      decodedIdToken,
      decodedAccessToken,
      audience: this.audience,
      scope: this.scope
    };

    return {
      appState: transaction.appState
    };
  }

  async handleRefreshOnInit() {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN);

    const params: Record<string, string> = {
      grant_type: REFRESH_TOKEN,
      client_id: this.options.client_id
    };
    if (refreshToken) {
      params.refresh_token = refreshToken;
    }

    const authResult = await oauthToken(this.tokenEndpoint, params);

    const idToken = this.assertTokenPresent(authResult.id_token, ID_TOKEN);
    localStorage.setItem(ID_TOKEN, idToken);

    const accessToken = authResult.access_token;
    if (!accessToken && isManagementViewEnabled()) {
      throw new Error(`Missing ${ACCESS_TOKEN} in authentication response`);
    }
    if (accessToken) {
      localStorage.setItem(ACCESS_TOKEN, accessToken);
    }

    if (authResult.refresh_token) {
      localStorage.setItem(REFRESH_TOKEN, authResult.refresh_token);
    }

    const decodedIdToken = this._verifyIdToken(idToken);
    decodedIdToken.user.id = decodedIdToken.user[SUBJECT_OF_TOKEN];

    const decodedAccessToken = accessToken
      ? decode(accessToken)
      : decodedIdToken;
    if (
      !this.userHasRequiredScopeRole(decodedAccessToken) &&
      !this.userHasRequiredScopeRole(decodedIdToken)
    ) {
      throw new Error('Unauthorized user');
    }

    const cacheEntry = {
      ...authResult,
      decodedIdToken,
      decodedAccessToken,
      audience: this.audience,
      scope: this.scope
    };
    this.cache.save(cacheEntry);

    window.authCache = {
      ...authResult,
      decodedIdToken,
      decodedAccessToken,
      audience: this.audience,
      scope: this.scope
    };
  }

  handleStaticAccessToken(accessToken: string) {
    const token = this.assertTokenPresent(accessToken, ACCESS_TOKEN);
    localStorage.setItem(ACCESS_TOKEN, token);

    const decodedAccessToken = decode(token);
    decodedAccessToken.user.id = decodedAccessToken.user[SUBJECT_OF_TOKEN];

    if (!this.userHasRequiredScopeRole(decodedAccessToken)) {
      throw new Error('Unauthorized user');
    }

    const cacheEntry = {
      decodedIdToken: decodedAccessToken,
      decodedAccessToken,
      audience: this.audience,
      scope: this.scope
    };
    this.cache.save(cacheEntry);

    window.authCache = {
      decodedIdToken: decodedAccessToken,
      decodedAccessToken,
      audience: this.audience,
      scope: this.scope
    };
  }

  userHasRequiredScopeRole(decodedToken: ReturnType<typeof decode>) {
    const scopes = this.scope.split(' ');
    const profile = scopes[scopes.length - 1];
    const roles = decodedToken.user[profile];
    return this.requiredRole === null || roles?.includes(this.requiredRole);
  }

  isAuthenticated() {
    const user = this.getUser();
    return !!user;
  }

  logout() {
    const isManagementView = isManagementViewEnabled();
    const params: Record<string, string> = {
      client_id: this.options.client_id,
      ...(isManagementView
        ? { logout_uri: this.options.redirect_uri, goto: this.options.redirect_uri }
        : { post_logout_redirect_uri: this.options.redirect_uri })
    };

    // Try to get id_token from localStorage first, then fallback to window.authCache
    let idToken = localStorage.getItem(ID_TOKEN);
    if (!idToken && window.authCache?.id_token) {
      idToken = window.authCache.id_token;
    }
    
    // Ensure id_token_hint is always included when available
    if (idToken) {
      params.id_token_hint = idToken;
    } else if (isManagementViewEnabled()) {
      console.warn('No id_token found for logout with management view enabled');
    }

    const logoutUrl = this._url(this.logoutEndpoint, params);

    localStorage.removeItem(ID_TOKEN);
    localStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem(REFRESH_TOKEN);

    if (isManagementView) {
      fetch(logoutUrl, {
        method: 'GET',
        mode: 'no-cors',
        credentials: 'include',
        keepalive: true
      })
        .catch((error) => {
          console.warn('Management logout request failed', error);
        })
        .finally(() => {
          window.location.assign(this.options.redirect_uri);
        });
      return;
    }

    window.location.assign(logoutUrl);
  }

  getCacheEntry() {
    return this.cache.get({
      scope: this.scope,
      audience: this.audience
    });
  }
}
